import React, { useCallback, useState } from "react";
import useSWR from "swr";
import { fetchOrderList, fetchTokenCandles } from "~/api/endpoints";
import { useSnackbar } from "~/Provider/Snackbar";
import { K_TYPE } from "./constants";
import { Token } from "~/types";
import LineChart from "./LineChart";
import KLine from "./KLine";
import { clsx } from "clsx";
import { ORDER_STATUS } from "../constants";
import { isNil, last } from "lodash-es";
import BigNumber from "bignumber.js";

type TokenChartsProps = {
    activeTypeCode: string;
    activeCode: string | number;
    chain_id: string | null;
    contract: string | null;
    tokenInfo?: Token;
    updateTokenPrice: (newPrice: number) => void
};

const TokenCharts: React.FC<TokenChartsProps> = ({
    activeCode,
    activeTypeCode,
    chain_id,
    contract,
    tokenInfo,
    updateTokenPrice,
}) => {
    const snapbar = useSnackbar();

    const isLite = activeTypeCode === K_TYPE[1].value;

    const initData = (args: any) => {
        const [_, chain_id, contract, bar_type, ts, size] = args;
        return fetchTokenCandles(chain_id, contract, bar_type, ts, size).then(
            (res) => {
                const { result = [], err_msg } = res?.data || {};
                if (err_msg && err_msg.length > 0) {
                    snapbar.error(err_msg);
                }
                return result;
            }
        );
    };

    const { data: result, isLoading } = useSWR(
        ["k-detail:api-line-chart", chain_id, contract, activeCode, 0, 200],
        initData,
        {
            refreshInterval: 8000,
            fallbackData: [],
            onSuccess(data) {
                const lastOne = last(data)
                if (lastOne) {
                    const lastPrice = lastOne.open_usd
                    if (isNil(tokenInfo?.token_market?.price) || tokenInfo?.token_market?.price === 0) {
                        updateTokenPrice(lastPrice)
                    }
                }
            },
        }
    );

    const queryOrderList = useCallback(([_, chain_id, contract]: any) => {
        if (chain_id == null || contract == null) return [];
        const params = {
            chain_id,
            contract,
        };
        return fetchOrderList(params)
            .then((res) => res?.data?.list ?? [])
            .then((list) =>
                list.filter((item) => item.status === ORDER_STATUS.COMPLETED)
            );
    }, []);

    const { data: orderList } = useSWR(
        [
            "k-detail:queryOrderList",
            tokenInfo?.token_base?.chain_id,
            tokenInfo?.token_base?.contract,
        ],
        queryOrderList,
        {
            fallbackData: [],
        }
    );

    const [isLargeMode, setIsLargeMode] = useState(false);

    const hasInitializedTokenInfo =
        tokenInfo && tokenInfo?.token_base?.contract?.length > 0;
    const hasInitializedTokenPrice =
        tokenInfo && tokenInfo?.token_market?.price != null;

    if (!hasInitializedTokenInfo || !hasInitializedTokenPrice || isLoading) {
        return <div className="w-full h-[284px]" />;
    }

    const precision = guessTokenPrecision(tokenInfo?.token_market?.price);

    return (
        <div
            className={clsx(
                "w-full relative",
                isLargeMode ? "h-[384px]" : "h-[284px]"
            )}
        >
            <div
                className={clsx(
                    "absolute top-0 right-0 bottom-0 left-0",
                    isLite
                        ? "visible z-10 opacity-100"
                        : "invisible z-0 opacity-0"
                )}
            >
                <LineChart
                    tokenInfo={tokenInfo}
                    initialData={result}
                    orderList={orderList}
                />
            </div>
            <div
                className={clsx(
                    "absolute top-0 right-0 bottom-0 left-0",
                    !isLite
                        ? "visible z-10 opacity-100"
                        : "invisible z-0 opacity-0"
                )}
            >
                <KLine
                    tokenInfo={tokenInfo}
                    chain_id={chain_id}
                    contract={contract}
                    activeCode={activeCode}
                    initialData={result}
                    visible={!isLite}
                    isLargeMode={isLargeMode}
                    precision={precision}
                    setIsLargeMode={setIsLargeMode}
                    orderList={orderList}
                />
            </div>
        </div>
    );
};

export default TokenCharts;

function guessTokenPrecision(price: number) {
    const bigNumberValue = new BigNumber(price);
    const [, decimal] = bigNumberValue.toFixed().split(".");

    let zeros = 0;

    if (decimal && decimal.length > 0) {
        for (let i = 0; i < decimal.length; i++) {
            if (decimal[i] === "0") {
                zeros++;
            } else {
                break;
            }
        }
    }

    if (bigNumberValue.gte(10)) {
        return 2;
    } else {
        return zeros > 2 ? Math.min(zeros + 2, 10) : 4;
    }
}
